import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  OnChanges,
  output,
  signal,
  SimpleChanges,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbCollapseModule } from '@ng-bootstrap/ng-bootstrap';

import { InternalNote } from './internal-note.model';

import { IconComponent } from '../design-system/components/icon/icon.component';

@Component({
  selector: 'lib-internal-notes',
  standalone: true,
  imports: [DatePipe, ReactiveFormsModule, NgbCollapseModule, IconComponent],
  templateUrl: './internal-notes.component.html',
  styleUrl: './internal-notes.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InternalNotesComponent implements OnChanges {
  // Include the entity type, e.g. "owner:123" or "animal:456".
  readonly contextKey = input.required<string>();
  readonly notes = input<readonly InternalNote[]>([]);
  readonly title = input('Internal notes');
  readonly placeholder = input('Note for shelter staff');
  readonly maxLength = input(240);
  readonly pageSize = input(3);
  readonly allowAdd = input(true);
  readonly isLoading = input(false);
  readonly isSaving = input(false);
  readonly loadError = input<string | null>(null);
  readonly saveError = input<string | null>(null);
  // Increment only after a successful save; failed saves retain the draft.
  readonly resetKey = input(0);
  readonly dateFormat = input('MM/dd/yyyy');
  readonly timezone = input<string | undefined>(undefined);

  readonly addNote = output<string>();
  readonly retryLoad = output<void>();

  readonly isCollapsed = signal(false);
  private readonly visibleCount = signal(3);

  readonly noteControl = new FormControl('', {
    nonNullable: true,
    validators: [Validators.required, Validators.maxLength(240)],
  });
  private readonly draft = toSignal(this.noteControl.valueChanges, {
    initialValue: '',
  });

  readonly isNotesExpanded = computed(
    () => this.visibleCount() > this.notesPerPage(),
  );
  readonly canExpandNotes = computed(
    () => this.sortedNotes().length > this.notesPerPage(),
  );

  readonly characterLimit = computed(() =>
    Math.max(1, Math.floor(this.maxLength()) || 240),
  );
  readonly notesPerPage = computed(() =>
    Math.max(1, Math.floor(this.pageSize()) || 3),
  );
  readonly remainingCharacters = computed(
    () => this.characterLimit() - this.draft().length,
  );
  readonly canSubmit = computed(
    () =>
      this.allowAdd() &&
      !this.isLoading() &&
      !this.isSaving() &&
      !this.loadError() &&
      this.draft().trim().length > 0 &&
      this.draft().length <= this.characterLimit(),
  );
  readonly sortedNotes = computed(() =>
    [...this.notes()].sort(
      (left, right) =>
        this.timestamp(right.createdDate) - this.timestamp(left.createdDate),
    ),
  );
  readonly visibleNotes = computed(() =>
    this.isNotesExpanded()
      ? this.sortedNotes()
      : this.sortedNotes().slice(0, this.notesPerPage() + 1),
  );

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['contextKey']) {
      this.noteControl.reset();
      this.visibleCount.set(this.notesPerPage());
      this.isCollapsed.set(false);
    }

    if (changes['resetKey'] && !changes['resetKey'].firstChange) {
      this.noteControl.reset();
      this.visibleCount.set(this.notesPerPage());
    }

    if (changes['pageSize']) {
      this.visibleCount.set(this.notesPerPage());
    }

    if (changes['maxLength']) {
      this.noteControl.setValidators([
        Validators.required,
        Validators.maxLength(this.characterLimit()),
      ]);
      this.noteControl.updateValueAndValidity();
    }

    if (changes['isSaving'] || changes['allowAdd']) {
      if (this.isSaving() || !this.allowAdd()) {
        this.noteControl.disable({ emitEvent: false });
      } else {
        this.noteControl.enable({ emitEvent: false });
      }
    }
  }

  submitNote(): void {
    if (!this.canSubmit()) {
      this.noteControl.markAsTouched();
      return;
    }

    this.addNote.emit(this.noteControl.getRawValue().trim());
  }

  showMoreNotes(): void {
    this.visibleCount.update((count) => count + this.notesPerPage());
  }

  toggleNotes(): void {
    if (this.isNotesExpanded()) {
      this.visibleCount.set(this.notesPerPage());
    } else {
      this.showMoreNotes();
    }
  }

  private timestamp(value: string): number {
    const timestamp = Date.parse(value);
    return Number.isFinite(timestamp) ? timestamp : 0;
  }
}
